<template>
  <b-modal
    id="contract-add-new"
    :title="`${isEdit ? 'Muokkaa sopimusta' : 'Luo uusi sopimus'}`"
    :no-close-on-backdrop="true"
    cancel-variant="outline-secondary"
    scrollable
    size="lg"
    @show="initData"
    @hide="() => {
      resetData()
      $emit('hide')
    }"
  >
    <validation-observer ref="rules">
      <b-form
        ref="form"
        @submit.stop.prevent="handleCreate"
      >
        <b-row>
          <b-col
            cols="12"
          >
            <b-form-group
              :label="$t('contract.labels.contractName')"
              label-for="contractName"
            >
              <validation-provider
                #default="{ errors }"
                name="Sopimuksen nimi"
                rules="required"
              >
                <b-form-input
                  id="contractName"
                  v-model.trim="contractName"
                  :state="errors.length > 0 ? false : null"
                  :placeholder="$t('contract.placeholders.contractName')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <hr>

        <b-row>
          <div style="display: flex; flex-direction: row;">
            <b-col cols="6">
              <b-form-group
                label="Alkaa"
                label-for="start"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Alkaa"
                  rules="required"
                >
                  <flat-pickr
                    v-model="start"
                    class="form-control"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="Loppuu"
                label-for="end"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Loppuu"
                  rules="required"
                >
                  <flat-pickr
                    v-model="end"
                    class="form-control"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                
              </b-form-group>
            </b-col>
          </div>
        </b-row>

        <b-row v-if="appConfig.type === 'hw-admin'">
          <b-col
            cols="12"
          >
            <b-form-checkbox
              v-model="noOverHours"
              :value="true"
              :unchecked-value="false"
            >
              {{ $t('contract.labels.noOverHours') }}
            </b-form-checkbox>
          </b-col>
          <b-col
            cols="12"
            style="margin-top: 10px;"
          >
            <b-form-checkbox
              v-model="isMonthlySalary"
              :value="true"
              :unchecked-value="false"
            >
              {{ $t('contract.labels.monthlySalary') }}
            </b-form-checkbox>
          </b-col>
        </b-row>

        <hr>

        <b-row>
          <b-col
            cols="3"
            
          >
            <span style="display: flex; flex-direction: row; align-items: flex-end; width: 130px;">
              <b-form-group>
                <label style="width: 200px;">Palkaton työaika</label>
                <b-form-input v-model="unpaidTime" style="height: 40px; width: 50px; text-align: center;" />
              </b-form-group>
              <p style="margin-bottom: 22px;">minuuttia</p>
            </span>
          </b-col>
        </b-row>

        <hr>

        <b-row class="mt-1">
          <b-col v-if="appConfig.type === 'hw-admin'" cols="12">
            <app-collapse style="width: 100%; margin: 0 auto; padding: 0">
              <app-collapse-item
                :title="$t('contract.labels.price')"
                style="text-transform: capitalize"
                is-visible
              >
                <validation-provider
                  #default="{ errors }"
                  name="Price"
                  rules="required"
                >
                  <div
                    v-for="(item, index) in priceItems"
                    :key="`price-item-${index}`"
                    class="d-flex align-items-end"
                  >
                    <div class="w-100">
                      <b-row>
                        <b-col cols="9">
                          <b-form-group
                            :label="$t('name')"
                          >
                            <v-select
                              v-model.trim="item.name"
                              :options="typeReqOptions"
                              :reduce="val => val"
                              :placeholder="$t('contract.placeholders.priceName')"
                              :disabled="item.name === 'default'"
                              @option:deselected="item.name = ''"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="3">
                          <b-form-group
                            :label="$t('contract.labels.price')"
                          >
                            <b-form-input
                              v-model.trim="item.value"
                              :placeholder="$t('contract.placeholders.priceValue')"
                              type="number"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </div>
                    <div>
                      <b-button
                        v-if="item.name !== 'default'"
                        v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                        size="sm"
                        variant="outline-danger"
                        class="mb-1 ml-2"
                        style="height: 37px;"
                        @click="handleRemoveRowItem(index, 'price')"
                      >
                        <feather-icon
                          icon="XIcon"
                          size="18"
                        />
                      </b-button>
                      <div
                        v-else
                        style="width: 69px"
                      />
                    </div>
                  </div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                <b-row>
                  <b-col cols="12">
                    <b-button
                      variant="outline-primary"
                      block
                      @click="handleAddRowItem('price')"
                    >
                      {{ $t('contract.btn.addPrice') }}
                    </b-button>
                  </b-col>
                </b-row>
              </app-collapse-item>
            </app-collapse>
          </b-col>
        </b-row>

        <b-row class="mt-1">
          <b-col cols="12">
            <app-collapse style="width: 100%; margin: 0 auto; padding: 0">
              <app-collapse-item
                :title="$t('contract.labels.salary_short')"
                style="text-transform: capitalize"
                is-visible
              >
                <validation-provider
                  #default="{ errors }"
                  name="Salary"
                  rules="required"
                >
                  <div
                    v-for="(item, index) in salaryItems"
                    :key="`salary-item-${index}`"
                    class="d-flex align-items-end"
                  >
                    <div class="w-100">
                      <b-row>
                        <b-col cols="9">
                          <b-form-group
                            :label="$t('profession')"
                          >
                            <v-select
                              v-model.trim="item.name"
                              :options="typeReqOptions"
                              :reduce="val => val"
                              :disabled="appConfig.type === 'hw-admin' && item.name === 'default'"
                              :clearable="false"
                              @option:deselected="item.name = ''"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col cols="3">
                          <b-form-group
                            :label="$t('contract.labels.salary_short')"
                          >
                            <b-form-input
                              v-model.trim="salaryItems[index].value"
                              placeholder="€/h"
                              type="number"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </div>
                    <div>
                      <b-button
                        v-if="item.name !== 'default'"
                        v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                        size="sm"
                        variant="outline-danger"
                        class="mb-1 ml-2"
                        style="height: 37px;"
                        @click="handleRemoveRowItem(index, 'salary')"
                      >
                        <feather-icon
                          icon="XIcon"
                          size="18"
                        />
                      </b-button>
                      <div
                        v-else
                        style="width: 69px"
                      />
                    </div>
                  </div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                <b-row>
                  <b-col cols="12">
                    <b-button
                      variant="outline-primary"
                      block
                      @click="handleAddRowItem('salary')"
                    >
                      {{ $t('contract.btn.addSalary') }}
                    </b-button>
                  </b-col>
                </b-row>
              </app-collapse-item>
            </app-collapse>
          </b-col>
        </b-row>

      </b-form>
    </validation-observer>
    <template #modal-footer="{ cancel }">
      <b-button
        variant="outline-secondary"
        @click="cancel"
      >
        {{ $t('cancel') }}
      </b-button>
      <b-button
        variant="primary"
        :disabled="!contractName || (appConfig.type === 'hw-admin' && checkInvalidPrice) || checkInvalidSalary || isSubmitting || !start || !end"
        @click="handleCreate"
      >
        {{ isEdit ? $t('save') : $t('create') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BButton, BModal, BRow, BCol, BForm, BFormGroup, BFormInput, BFormCheckbox,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { required } from '@validations'
import AppCollapse from '@hw-ui/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@hw-ui/components/app-collapse/AppCollapseItem.vue'
import flatPickr from 'vue-flatpickr-component'

export default {
  components: {
    BButton,
    BModal,
    BRow,
    BCol,
    BForm,
    vSelect,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    ValidationObserver,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    flatPickr,
  },
  directives: {
    Ripple,
  },
  props: {
    docItem: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      required,
      processer: {},
      contractName: '',
      noOverHours: false,
      isMonthlySalary: false,
      unpaidTime: 0,
      start: null,
      end: null,
      priceItems: [{ name: 'default', value: null }],
      salaryItems: [],
      updates: {
        content: [],
        date: '',
        processer: '',
        state: '',
      },
      isSubmitting: false,
    }
  },
  computed: {
    isEdit() {
      return !!this.docItem
    },
    selectedAccount() {
      return this.$store.state.appAccount.selectedAccount
    },
    typeReqOptions() {
      return this.$store.state.app.appConfig.type === 'hw-admin'
        ? this.$store.state.appAccount.roles.map(role => role.label).concat(['Love'])
        : this.$store.state.appAccount.selectedAccount.value.professions
    },
    appConfig() {
      return this.$store.state.app.appConfig || {}
    },
    checkInvalidPrice() {
      let inValid = false
      if (!this.priceItems || !this.priceItems.length) {
        inValid = true
      } else {
        const match = this.priceItems.findIndex(item => item.name.length < 1 || !item.value || item.value < 1)
        if (match !== -1) {
          inValid = true
        }
      }
      return inValid
    },
    checkInvalidSalary() {
      let inValid = false
      if (!this.salaryItems || !this.salaryItems.length) {
        inValid = true
      } else {
        const match = this.salaryItems.findIndex(item => item.name.length < 1 || !item.value || item.value < 1)
        if (match !== -1) {
          inValid = true
        }
      }
      return inValid
    },
  },
  beforeCreate() {
    this.$store.dispatch('appAuth/getCurrentUser')
      .then(res => {
        const user = JSON.parse(JSON.stringify(res))
        this.processer = {
          firstName: user.firstName,
          lastName: user.lastName,
        }
      })
  },
  methods: {
    initData() {
      if (this.isEdit) {
        this.contractName = this.docItem.name
        this.noOverHours = this.docItem.noOverHours ? this.docItem.noOverHours : false
        this.unpaidTime = this.docItem.unpaidTime || 0
        const priceItems = []
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of Object.entries(this.docItem.price)) {
          priceItems.push({
            name: key,
            value,
          })
        }
        const salaryItems = this.appConfig.type === 'hw-admin'
          ? [{ name: 'default', value: null }]
          : []
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of Object.entries(this.docItem.salary)) {
          salaryItems.push({
            name: key,
            value,
          })
        }

        if (this.docItem.start && this.docItem.end) {
          const startDate = this.docItem.start.toDate()
          const endDate = this.docItem.end.toDate()

          this.start = `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`
          this.end = `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`
        }

        this.priceItems = priceItems
        this.salaryItems = salaryItems
      } else {
        this.resetData()
      }
    },
    resetData() {
      this.contractName = ''
      this.noOverHours = false
      this.isMonthlySalary = false
      this.unpaidTime = 0
      this.start = null
      this.end = null
      this.priceItems = [{ name: 'default', value: null }]
      this.salaryItems = []
    },
    handleRemoveRowItem(index, type = 'price') {
      if (index === 0) {
        return
      }
      if (type === 'price') {
        this.priceItems.splice(index, 1)
      } else {
        this.salaryItems.splice(index, 1)
      }
    },
    handleAddRowItem(type = 'price') {
      if (type === 'price') {
        this.priceItems.push({
          name: '',
          value: null,
        })
      } else {
        this.salaryItems.push({
          name: '',
          value: null,
        })
      }
    },
    handleCreate() {
      this.$refs.rules.validate().then(async success => {
        if (success) {
          this.isSubmitting = true
          if (this.isEdit) {
            this.updates = {
              content: [],
              date: new Date(),
              processer: `${this.processer.firstName} ${this.processer.lastName}`,
              state: 'updated',
            }
            if (this.contractName !== this.docItem.name) {
              this.updates.content.push(`Name: ${this.contractName}`)
            }
            if (this.noOverHours !== this.docItem.noOverHours) {
              this.updates.content.push(`No Over Hours: ${this.noOverHours}`)
            }

            this.salaryItems.forEach(salaryItem => {
              if (!this.docItem.salary[salaryItem.name] || (this.docItem.salary[salaryItem.name] && salaryItem.value !== this.docItem.salary[salaryItem.name])) {
                this.updates.content.push(`Salary ${salaryItem.name}: ${salaryItem.value}`)
              }
            })

            this.priceItems.forEach(priceItem => {
              if (!this.docItem.price[priceItem.name] || (this.docItem.price[priceItem.name] && priceItem.value !== this.docItem.price[priceItem.name])) {
                this.updates.content.push(`Price ${priceItem.name}: ${priceItem.value}`)
              }
            })

            Object.keys(this.docItem.price).forEach(key => {
              if (this.priceItems.findIndex(priceItem => priceItem.name === key) === -1) {
                this.updates.content.push(`Removed: Price ${key}`)
              }
            })

            Object.keys(this.docItem.salary).forEach(key => {
              if (this.salaryItems.findIndex(salaryItem => salaryItem.name === key) === -1) {
                this.updates.content.push(`Removed: Salary ${key}`)
              }
            })
          } else {
            this.updates = {
              content: ['Created contract'],
              date: new Date(),
              processer: `${this.processer.firstName} ${this.processer.lastName}`,
              state: 'created',
            }
          }
          const updates = this.isEdit ? [this.updates, ...(this.docItem.updates ? this.docItem.updates : [])] : [this.updates]
          const salary = {}
          const price = {}

          const start = new Date(this.start)
          start.setHours(0)
          start.setMinutes(0)
          const end = new Date(this.end)
          end.setHours(23)
          end.setMinutes(59)

          this.salaryItems.forEach(salaryItem => {
            salary[salaryItem.name.toLowerCase()] = +salaryItem.value
          })

          this.priceItems.forEach(priceItem => {
            price[priceItem.name.toLowerCase()] = +priceItem.value
          })

          const data = {
            id: this.isEdit ? this.docItem.id : null,
            accountId: this.selectedAccount.value.id,
            created: this.isEdit ? this.docItem.created : new Date(),
            name: this.contractName,
            noOverHours: this.noOverHours,
            isMonthlySalary: this.isMonthlySalary,
            unpaidTime: this.unpaidTime || 0,
            customerNumber: 1234,
            // eslint-disable-next-line no-nested-ternary
            deleted: this.isEdit && this.docItem.deleted
              ? this.docItem.deleted
              : false,
            price,
            salary,
            type: 'contract',
            updates,
            start,
            end,
          }
          this.$emit('create-or-update-contract', {
            data,
            callback: () => {
              this.$bvModal.hide('contract-add-new')
              this.isSubmitting = false
            },
          })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
  @import '@hw-ui/scss/vue/libs/vue-flatpicker.scss';
  @import '@hw-ui/scss/vue/libs/vue-select.scss';

  ::v-deep .modal-lg {
    max-width: 550px;
  }
</style>
